import Vue from "vue";
import Vuex from "vuex";
import { Auth } from "aws-amplify";
import createPersistedState from "vuex-persistedstate";
import axios from "axios";
import router from "../router";
import cmsLatestNews from "./modules/cms/latestNews";
import cmsGamesLibrary from "./modules/cms/gamesLibrary";
import cmsGlossary from "./modules/cms/glossary";
import cmsDocuments from "./modules/cms/documents";
import userAdmin from "./modules/user/userAdmin";
import player from "./modules/player/player";
import cmsNotifications from "./modules/cms/notifications";
import content from "./modules/cms/content";
import cmsGameAssets from "./modules/cms/gameAssets";

Vue.use(Vuex);

const store = new Vuex.Store({
  // Spliting the store into modules to prevent store becoming bloated
  // https://vueschool.io/lessons/vuex-modules
  modules: {
    latestNews: cmsLatestNews,
    gamesLibrary: cmsGamesLibrary,
    glossary: cmsGlossary,
    documents: cmsDocuments,
    userAdmin: userAdmin,
    player: player,
    cmsNotifications: cmsNotifications,
    content: content,
    gameAssets: cmsGameAssets,
  },
  // Plugins
  plugins: [createPersistedState()],
  // Base State
  state: {
    username: "",
    userDetails: {},
    selectedOperator: {
      id: 0,
      name: "",
    },
    networkFilter: {
      id: 0,
      name: "",
    },
    siteFilter: {
      id: 0,
      name: "",
    },
    selectedNetworkId: 0,
    selectedSiteId: 0,
    selectedView: { label: "Day", value: "day" },
    liveAcquisitionFilter: {},
    playerNetworkId: null,
    amlMilestoneKey: null,
    cognitoJwtToken: "",
    playerId: null,
    siteNotification: true,
    userTypeRole: [],
  },
  // Synchronous
  mutations: {
    SET_USERNAME_AND_TOKEN(state, payload) {
      (state.username = payload.username),
        (state.cognitoJwtToken = payload.jwtToken);
    },
    SET_USER_DETAILS(state, payload) {
      state.userDetails = payload.userDetails;
    },
    SET_OPERATOR(state, payload) {
      state.selectedOperator.id = payload.id;
      state.selectedOperator.name = payload.name;
    },
    SET_NETWORK(state, payload) {
      state.selectedNetworkId = payload.selectedNetworkId;
      state.networkFilter = {
        id: payload.id,
        name: payload.name,
      };
    },
    SET_SITE(state, payload) {
      state.selectedSiteId = payload.selectedSiteId;
      state.siteFilter = {
        id: payload.id,
        name: payload.name,
      };
    },
    SET_VIEW(state, payload) {
      state.selectedView = payload.selectedView;
    },
    SET_LIVE_ACQUISITION_FILTER_CHANNEL(state, payload) {
      state.liveAcquisitionFilter = payload;
    },
    SET_LIVE_ACQUISITION_FILTER_TIME(state, payload) {
      state.liveAcquisitionFilter = payload;
    },
    SET_LIVE_ACQUISITION_FILTER_BUTTON(state, payload) {
      state.liveAcquisitionFilter = payload;
    },
    SET_PLAYER_NETWORK_ID(state, payload) {
      state.playerNetworkId = payload.playerNetworkId;
    },
    SET_AML_MILESTONE_KEY(state, payload) {
      state.amlMilestoneKey = payload.amlMilestoneKey;
    },
    SET_PLAYER_ID(state, payload) {
      state.playerId = payload.playerId;
    },
    SET_SITE_NOTIFICATION(state, payload) {
      state.siteNotification = payload.siteNotification;
    },
    SET_USER_TYPE_ROLE(state, payload) {
      state.userTypeRole = payload;
    },
  },
  // Asynchronous
  actions: {
    // Gets the logged in user, if they are not logged in then sets flag to avoid auth errors down the line.
    fetchCurrentUsername(context) {
      Auth.currentAuthenticatedUser()
        .then((user) => {
          context.commit("SET_USERNAME_AND_TOKEN", {
            username: user.attributes.email,
            jwtToken: user.signInUserSession.accessToken.jwtToken,
          });
        })
        .catch((error) => {
          console.log(error);
        });
    },
    // fetchCurrentUserDetails(context) {
    //     try {
    //         Auth.currentAuthenticatedUser()
    //             .then((user) => {
    //                 axios
    //                     .get(`/api/users/${user.attributes.email}`)
    //                     .then(response => response.json())
    //                     .then(userDetails => context.commit('SET_USER_DETAILS', { userDetails: userDetails }))
    //                     .catch(() => console.log("Error: action fetchCurrentUserDetails"));
    //             }).catch(error => {
    //                 console.log("Get User", error);
    //                 this.$router.push("/not-authorised");
    //             });
    //     } catch (error) {
    //         console.log(error);
    //     }
    // },

    fetchCurrentUserDetails(context) {
      Auth.currentAuthenticatedUser().then((user) => {
        try {
          axios
            .get(`/api/users/${user.attributes.email}`)
            .then((response) => {
              return response.data;
            })
            .then((userDetails) =>
              context.commit("SET_USER_DETAILS", { userDetails: userDetails })
            )
            .catch((error) => {
              console.log("Get User Details", error);
              router.push("/not-authorised");
            });
        } catch (error) {
          console.log(error);
        }
      });
    },

    logout(context) {
      Auth.signOut()
        .then(() => {
          // Reset values on log out
          context.commit("SET_USERNAME_AND_TOKEN", {
            username: "",
            jwtToken: "",
          });
          context.commit("SET_USER_DETAILS", { userDetails: {} });
          context.commit("SET_OPERATOR", { selectedOperator: {} });
          context.commit("SET_NETWORK", { selectedNetworkId: 0 });
          context.commit("SET_SITE", { selectedSiteId: 0 });
          context.commit("SET_PLAYER_NETWORK_ID", { playerNetworkId: null });
          context.commit("SET_VIEW", { selectedView: {} });
          context.commit("SET_PLAYER_ID", { playerId: "" });
          context.commit("SET_CATEGORY", {
            selectedCategoryName: "All",
            categoryId: null,
          });
          context.commit("SET_SEARCH_QUERY", { searchQuery: null });
          context.commit("SET_PROVIDERS", { selectedProviders: [] });
          context.commit("SET_SITE_NOTIFICATION", { siteNotification: true });
          context.commit("SET_USER_TYPE_ROLE", []);
          context.commit("SET_USER_TYPE_NEW", { selectedUserType: [] });
          context.commit("SET_CATEGORY_NEW", { selectedCategory: null });
          context.commit("SET_PROVIDER_NEW", { selectedProviders: [] });
          context.commit("SET_SEARCH_NEW", { selectedSearch: null });

          //check if cpUser present
          const rememberMeEmail = localStorage.getItem("cpUser");

          //kept it so present functionality won't get effect
          localStorage.clear();

          //restor cpUser value
          if (rememberMeEmail != null)
            localStorage.setItem("cpUser", rememberMeEmail);
        })
        .catch(() => console.log("Error: action logout"));
    },

    async fetchData({ getters }, payload) {
      const filterOptions = setFilterOptions(payload.filterOptions, getters);
      try {
        const response = await axios.post(
          `/api/${payload.report}/${payload.dataset}`,
          filterOptions
        );
        return response;
      } catch (error) {
        if (error.response.status === 419) {
          this.dispatch("logout");
        } else {
          console.log(error);
        }
      }
    },

    // Fetch Player Network Data - AML
    async fetchNetworkData({ getters }, payload) {
      const filterOptions = setFilterOptionsNetworkId(
        payload.filterOptions,
        getters
      );
      try {
        const response = await axios.post(
          `/api/${payload.report}/${payload.dataset}`,
          filterOptions
        );
        return response;
      } catch (error) {
        if (error.response.status === 419) {
          this.dispatch("logout");
        } else {
          console.log(error);
        }
      }
    },

    // Submit exclusion data - AML
    async submitProcessPlayerData({ getters }, payload) {
      const formData = submitData(payload.form, getters);

      try {
        const response = await axios.post(
          `/api/${payload.report}/${payload.dataset}`,
          formData
        );
        return response;
      } catch (error) {
        if (error.response.status === 419) {
          this.dispatch("logout");
        } else {
          console.log(error);
        }
      }
    },

    // Submit email data - forms
    async submitEmailData({ getters }, payload) {
      const formData = submitData(payload.form, getters);
      try {
        const response = await axios.post(
          `/api/send-email/${payload.form.type}`,
          formData
        );
        return response;
      } catch (error) {
        if (error.response.status === 419) {
          this.dispatch("logout");
        } else {
          console.log(error);
        }
      }
    },
    // Submit token to backend to check if valid, if not log the user out
    async checkTokenValid({ getters }) {
      const data = { token: getters.getCognitoJwtToken };
      try {
        const response = await axios.post(`/api/reports/`, data);
        return response;
      } catch (error) {
        this.dispatch("logout");
        console.log(error);
      }
    },

    // Submit token to backend to check if valid, if not log the user out
    fetchPbiAccessToken({ getters }, payload) {
      const data = submitData(payload, getters);
      try {
        const response = axios.post(`/api/pbi/`, data);
        return response;
      } catch (error) {
        console.log(
          "Could not verify access token, log user out. Error message:" + error
        );
      }
    },

    // Submit token to backend to check if valid, if not log the user out
    async checkSessionValid({ getters }) {
      const data = { token: getters.getCognitoJwtToken };

      try {
        await axios.post(`/api/session/`, data);

        return true;
      } catch (error) {
        console.error("checkSessionValid", error);

        return false;
      }
    },
  },
  getters: {
    GET_CURRENT_USER: (state) => state.username,
    GET_CURRENT_USER_DETAILS: (state) => state.userDetails,
    GET_CURRENT_USER_ROLE_FUNCTIONS: (state) =>
      state.userDetails.role_functions,
    GET_SELECTED_OPERATOR: (state) => state.selectedOperator,
    GET_SELECTED_OPERATOR2: (state) => state.selectedOperator,
    GET_SELECTED_NETWORK: (state) => state.selectedNetworkId,
    GET_SELECTED_SITE: (state) => state.selectedSiteId,
    GET_LIVE_ACQUISITION_FILTER: (state) => state.liveAcquisitionFilter,

    getUserOperatorId: (state) => state.userDetails.operatorId,
    getUserNetworkId: (state) => state.userDetails.networkId,
    getCognitoJwtToken: (state) => state.cognitoJwtToken,
    getSelectedPlayerNetworkId: (state) => state.playerNetworkId,
    getUserId: (state) => state.userDetails.userId,
    getPlayerId: (state) => state.playerId,
    getPbiRoles: (state) => state.userDetails.pbiRoles,
    getSiteNotification: (state) => state.siteNotification,
    getUserTypeRole: (state) => state.userTypeRole,
    getHasB2BRole: (state) => state.userTypeRole.includes(79),
    getHasB2CRole: (state) => state.userTypeRole.includes(78),
  },
});

export default store;

function setFilterOptions(filterOptions, getters) {
  // If user has an operator ID, always use that as this means user should only ever see data for that operator.
  if (
    getters.getUserOperatorId ||
    !("operatorId" in filterOptions && filterOptions.operatorId)
  ) {
    filterOptions.operatorId = getters.getUserOperatorId;
  }

  // If user has an network ID, always use that as this means user should only ever see data for that network.
  if (
    getters.getUserNetworkId ||
    !("networkId" in filterOptions && filterOptions.networkId)
  ) {
    filterOptions.networkId = getters.getUserNetworkId;
  }

  // Add empty site IDs filter if not in the filter options.
  // Site ID passed as a list for future extensibility to multi-select list.
  if (!("siteIds" in filterOptions && filterOptions.siteIds)) {
    filterOptions.siteIds = [];
  }

  // Set the player id.
  if (
    getters.getPlayerId ||
    !("playerId" in filterOptions && filterOptions.playerId)
  ) {
    filterOptions.playerId = getters.playerId;
  }

  // Set the user User Id.
  if (getters.getUserId) {
    filterOptions.userId = getters.getUserId;
  }

  // Finally, add JWT token for validation in API.
  filterOptions.token = getters.getCognitoJwtToken;

  return filterOptions;
}

function setFilterOptionsNetworkId(filterOptions, getters) {
  // If a selected Player Network ID
  if (getters.getSelectedPlayerNetworkId) {
    filterOptions.playerNetworkId = getters.getSelectedPlayerNetworkId;
  }

  // Finally, add JWT token for validation in API.
  filterOptions.token = getters.getCognitoJwtToken;

  return filterOptions;
}

function submitData(data, getters) {
  // Finally, add JWT token for validation in API.
  data.token = getters.getCognitoJwtToken;

  return data;
}